import { notification } from "antd"
import { AxiosError } from "axios"
import { useMutation, useQuery, useQueryClient } from "react-query"
import { deleteUser, getActiveUsers, getDeletedUser, getDeletedUsers, getUser, getUsers, getUsersRoles, restoreUser, updateUser, updateUserPassword, UserDto, validateUserEmail } from "./usersApi"

export const useUsersQuery = (showDeletedUsers: boolean) => {
  return useQuery(
    showDeletedUsers ? ['users', 'deleted'] : ['users'],
    () => showDeletedUsers ? getDeletedUsers() : getUsers(),
    { refetchOnWindowFocus: false }
  )
}

export const useActiveUsersQuery = () => {
  return useQuery(['users', 'active'], getActiveUsers, { refetchOnWindowFocus: false })
}

export const useUserQuery = (userEmail: string, showDeletedUser: boolean) => {
  return useQuery(['user', userEmail], {
    queryFn: () => showDeletedUser ? getDeletedUser(userEmail) : getUser(userEmail),
    enabled: !!userEmail,
    refetchOnWindowFocus: false,
  })
}

export const useUpdateUserMutation = (userEmail: string, onSuccess: (updatedUser: UserDto) => void) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: updateUser,
    onSuccess: (data) => {
      queryClient.invalidateQueries(['users'])
      queryClient.invalidateQueries(['user', userEmail])
      onSuccess(data)
    },
    onError: (error: AxiosError) => {
      const errorMessage = error.response?.data as string;

      notification.error({
        message: 'Error updating user',
        description: errorMessage ?? 'An error occurred while updating the user.'
      })
    }
  })
}

export const useUsersQueryInvalidator = () => {
  const queryClient = useQueryClient()
  return { 
    invalidate: () => queryClient.invalidateQueries(['users'])
  }
}

export const useUpdatePasswordMutation = () => {
  return useMutation({
    mutationFn: (
      { email, newPassword, confirmNewPassword }: { email: string, newPassword: string, confirmNewPassword: string }
    ) => updateUserPassword(email, newPassword, confirmNewPassword),
    onSuccess: () => {
      notification.success({
        message: 'Password updated',
        description: 'The user password has been updated successfully.'
      })
    },
    onError: (error: AxiosError) => {
      const errorMessage = error.response?.data as string;

      notification.error({
        message: 'Error updating password',
        description: errorMessage ?? 'An error occurred while updating the user password.'
      })
    }
  })
}

export const useDeleteUserMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (email: string) => deleteUser(email),
    onSuccess: () => {
      queryClient.invalidateQueries(['users'])
    }
  })
}

export const useRestoreUserMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (email: string) => restoreUser(email),
    onSuccess: () => {
      queryClient.invalidateQueries(['users'])
    }
  })
}

export const useValidateUserEmailQuery = (email: string, isNewUser: boolean) => {
  return useQuery(['validateUserEmail', email], 
    () => validateUserEmail(email), 
    { 
      refetchOnWindowFocus: false,
      enabled: !!email && isNewUser
    }
  )
}

export const useUsersRolesQuery = () => {
  return useQuery(['userRoles'], () => getUsersRoles(), { refetchOnWindowFocus: false })
}